<template>
  <div>
    <v-dialog
      @keydown.esc="isOpen=false"
      persistent
      v-model="isOpen"
      width="416">
      <v-card>
        <div class="d-flex justify-end">
          <v-btn
            fab
            color="transparent"
            small
            depressed
            @click="closeDialog()"
            class="mr-1 mt-1"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-title class="pt-0 px-8 title pb-4">
          {{ this.form._id ? $t('expenses.outgoingCategories.editCategoryDialog.editTitle') : $t('expenses.outgoingCategories.editCategoryDialog.createTitle') }}
        </v-card-title>
        <v-card-text class="px-8 pb-8">
          <v-form v-model="isValid" ref="form">
            <v-row>
              <v-col class="py-0" cols="12">
                <div class="d-flex">
                  <v-select
                    rounded
                    outlined
                    dense
                    v-model="form.type"
                    :rules="rules.required"
                    item-text="name"
                    item-value="_id"
                    :items="types"
                    :label="$t('expenses.outgoingCategories.editCategoryDialog.type')"
                    @change="filterConcepts()"
                  />
                  <div class="pl-3">
                    <v-tooltip top v-if="expensesPermissions['create-outgoing-type']">
                      <template v-slot:activator="{ on }">
                        <v-btn
                          v-on="on"
                          fab
                          color="primary"
                          x-small
                          depressed
                          @click="openAddOutgoingTypeDialog()"
                        >
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Agregar tipo de egreso</span>
                    </v-tooltip>
                  </div>
                </div>
              </v-col>
              <v-col class="py-0" cols="12">
                <div class="d-flex">
                  <v-select
                    rounded
                    outlined
                    dense
                    v-model="form.concept"
                    :rules="rules.required"
                    item-text="name"
                    item-value="_id"
                    :items="filteredConcepts.length ? filteredConcepts : concepts"
                    :label="$t('expenses.outgoingCategories.editCategoryDialog.concept')"
                    @change="selectType()"
                  />
                  <div class="pl-3">
                    <v-tooltip top v-if="expensesPermissions['create-outgoing-concept']">
                      <template v-slot:activator="{ on }">
                        <v-btn
                          v-on="on"
                          fab
                          color="primary"
                          x-small
                          depressed
                          @click="openAddOutgoingConceptDialog()"
                        >
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Agregar concepto de egreso</span>
                    </v-tooltip>
                  </div>
                </div>
              </v-col>
              <v-col class="py-0" cols="12">
                <EditOutlinedInput
                  v-model="form.name"
                  :rules="rules.required"
                  :label="$t('expenses.outgoingCategories.editCategoryDialog.name')"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="d-flex justify-space-between px-10 pb-8">
          <Button size="x-small" type="outlined" @click="closeDialog()">
            {{ $t('expenses.outgoingCategories.editCategoryDialog.buttonCancel') }}
          </Button>
          <Button size="x-small" @click="create()">
            {{ this.form._id ? $t('expenses.outgoingCategories.editCategoryDialog.buttonEdit') : $t('expenses.outgoingCategories.editCategoryDialog.buttonCreate') }}
          </Button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <AddOutgoingType
      ref="addType"
      @updated="loadTypes"
    />

    <AddOutgoingConcept
      ref="addConcept"
      @updated="loadConceptsAndTypes"
    />

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import AddOutgoingType from '@/components/modals/AddOutgoingType'
import AddOutgoingConcept from '@/components/modals/AddOutgoingConcept'
import Button from '@/components/elements/Button'
import EditOutlinedInput from '@/components/elements/EditOutlinedInput'

export default {
  components: {
    AddOutgoingType,
    AddOutgoingConcept,
    EditOutlinedInput,
    Button
  },
  data () {
    return {
      overlay: false,
      isOpen: false,
      isValid: false,
      form: {
        type: null,
        concept: null,
        name: null
      },
      concepts: [],
      filteredConcepts: [],
      types: [],
      filteredType: [],
      rules: {
        required: [v => !!v || `Este campo ${this.$i18n.t('messages.isRequired')}`]
      }
    }
  },
  computed: {
    expensesPermissions () {
      return this.$store.state.login.permissions['expenses'].components['outgoing-category'].actions
    }
  },
  watch: {
    isOpen: async function (val) {
      if (val) {
        await this.loadTypes()
        await this.loadConcepts()
      }
    }
  },
  methods: {
    openDialog (category) {
      if (category) {
        this.form = {
          ...category
        }
      }
      this.isOpen = true
    },
    closeDialog () {
      this.isOpen = false
      this.form = {
        type: null,
        concept: null,
        name: null
      }
      this.filteredConcepts = []
      this.$refs.form.resetValidation()
    },
    async create () {
      this.$refs.form.validate()
      if (this.isValid) {
        this.overlay = true
        try {
          const payload = {
            ...this.form,
            type: 'outgoing',
            restaurant: this.$store.state.currentRestaurant.data._id
          }
          if (this.form._id) {
            await this.$api.put(`/v1/cashflows/${this.form._id}`, payload)
          } else {
            await this.$api.post(`/v1/cashflows`, payload)
          }
          this.$emit('updated')
          this.closeDialog()
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
      }
    },
    async loadConcepts (params = null) {
      params = params || {
        kind: 'outgoing',
        restaurant: this.$store.state.currentRestaurant.data._id
      }
      this.overlay = true
      try {
        const response = await this.$api.get(`/v1/cashflows/concepts`, params)
        this.concepts = response.data
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    async loadTypes (params = null) {
      params = params || {
        kind: 'outgoing',
        restaurant: this.$store.state.currentRestaurant.data._id
      }
      this.overlay = true
      try {
        const response = await this.$api.get(`/v1/cashflows/types`, params)
        this.types = response.data
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    async loadConceptsAndTypes () {
      await this.loadTypes()
      await this.loadConcepts()
      if (this.form.type) {
        const lastConcept = this.form.concept
        this.filterConcepts()
        if (lastConcept) {
          this.form.concept = lastConcept
        }
      }
    },
    openAddOutgoingTypeDialog () {
      this.$refs.addType.openDialog()
    },
    openAddOutgoingConceptDialog () {
      this.$refs.addConcept.openDialog()
    },
    filterConcepts () {
      this.form.concept = null
      this.filteredConcepts = this.concepts.filter(concept => concept.type === this.form.type)
    },
    selectType () {
      const selectedConcept = this.concepts.find(concept => concept._id === this.form.concept)
      this.form.type = selectedConcept.type ? this.types.find(type => type._id === selectedConcept.type) : ''
    }
  }
}
</script>

<style lang="scss" scoped>
  .title {
    font-weight: 500;
    font-size: 22px;
    color: #01D28E;
  }
</style>
